<script setup lang="ts">
// A doughnut chart for a low-cardinality mix: the share each member takes of
// the whole. The dashboard uses it where the members are a closed vocabulary
// (methods, control states, readings) rather than a ranked list, because a ring
// reads as "these parts make up the total" in a way a bar chart does not.
//
// Every slice is labelled in the legend with its own count and share — and
// counted again in the tooltip — so the reading never depends on telling two
// hues apart, and no slice is anonymous.
import { computed, ref } from "vue";
import { useElementSize } from "@vueuse/core";
import { Doughnut } from "vue-chartjs";
import {
	ArcElement,
	Chart as ChartJS,
	DoughnutController,
	Legend,
	Tooltip,
} from "chart.js";
import type {
	ChartData,
	ChartOptions,
	LegendItem,
	TooltipItem,
} from "chart.js";

import {
	chartFontSize,
	formatCount,
	legendOptions,
	sliceColor,
	TICK_COLOR,
} from "./theme";

ChartJS.register(DoughnutController, ArcElement, Tooltip, Legend);

const props = defineProps<{
	labels: string[];
	values: number[];
	/** One colour per slice; the palette in order when the caller has no
	 * vocabulary of its own to honour. */
	colors?: string[];
}>();

const emit = defineEmits<{ select: [index: number] }>();

// The box chart.js draws into, measured: its size is the only statement of how
// big this panel is, and the chart's type follows it — the same component is a
// narrow card in the analysis rail and a wide one in the enlarged overlay.
const boxRef = ref<HTMLElement | null>(null);
const box = useElementSize(boxRef);

// The chart's text size, derived once per box. Its own computed, so the options
// below are rebuilt only when the size actually changes: a resize that leaves
// the derived size alone needs no second chart.js layout pass, and the rail's
// first paint stays the only paint it gets.
const chartTextSize = computed(() =>
	chartFontSize(box.width.value, box.height.value),
);

const chartData = computed<ChartData<"doughnut">>(() => ({
	labels: props.labels,
	datasets: [
		{
			data: props.values,
			backgroundColor:
				props.colors ?? props.values.map((_value, index) => sliceColor(index)),
			borderColor: "#fff",
			borderWidth: 1,
		},
	],
}));

const total = computed(() =>
	props.values.reduce((sum, value) => sum + value, 0),
);

const chartOptions = computed<ChartOptions<"doughnut">>(() => {
	const size = chartTextSize.value;
	// One legend object, built once, so the spread below and the label rewrite
	// cannot disagree about the text size.
	const legend = legendOptions(true, size);
	return {
		responsive: true,
		maintainAspectRatio: false,
		// A shallow ring leaves room for the legend in a panel that is only a few
		// hundred pixels tall.
		cutout: "55%",
		onClick: (_event, elements) => {
			if (elements.length === 0) return;
			emit("select", elements[0].index);
		},
		plugins: {
			legend: {
				...legend,
				labels: {
					...legend.labels,
					// The ring carries the shape, the legend carries the numbers:
					// each entry reads "Label — count (share%)", computed from the
					// same values the tooltip uses, so a slice can be read at a
					// glance without hovering and without telling two hues apart.
					generateLabels: (chart): LegendItem[] => {
						const dataset = chart.data.datasets[0];
						const values = (dataset?.data ?? []).map((value) => Number(value));
						const sum = values.reduce((total, value) => total + value, 0);
						const fills = (dataset?.backgroundColor ?? []) as string[];
						return (chart.data.labels ?? []).map((label, index) => {
							const value = values[index] ?? 0;
							const share = sum > 0 ? (value / sum) * 100 : 0;
							return {
								text: `${String(label)} — ${formatCount(value)} (${share.toFixed(1)}%)`,
								fillStyle: fills[index] ?? sliceColor(index),
								strokeStyle: "#fff",
								lineWidth: 1,
								fontColor: TICK_COLOR,
								hidden: false,
								index,
							};
						});
					},
				},
			},
			tooltip: {
				callbacks: {
					// The count AND the share: a mix panel's question is both "how
					// many" and "what fraction of the work".
					label: (context: TooltipItem<"doughnut">) => {
						const value = Number(context.parsed);
						const share = total.value > 0 ? (value / total.value) * 100 : 0;
						return `${context.label}: ${formatCount(value)} (${share.toFixed(1)}%)`;
					},
				},
			},
		},
	};
});
</script>

<template>
	<div ref="boxRef" class="chart-box">
		<Doughnut :data="chartData" :options="chartOptions" />
	</div>
</template>

<style scoped>
/* The panel owns the height; the box fills it, so a taller grid row grows the
   chart rather than leaving a gap under it. */
.chart-box {
	position: absolute;
	inset: 0;
}
</style>
